import { db } from "../firebase/config";
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot,
  query,
  where,
  getDocs,
  writeBatch
} from "firebase/firestore";

// --- Date Helpers ---
export function getNextWeekDate(weekDateStr) {
  const d = new Date(weekDateStr + "T00:00:00");
  d.setDate(d.getDate() + 7);
  return d.toISOString().split("T")[0];
}

// --- Header Service Operations ---

export function subscribeToHeaders(userId, callback) {
  if (!userId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, "weekly_status_headers"), 
    where("userId", "==", userId)
  );
  return onSnapshot(q, (snapshot) => {
    const list = [];
    snapshot.forEach((doc) => {
      list.push({ id: doc.id, ...doc.data() });
    });
    // Sort client-side by order field, fallback to name
    list.sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));
    callback(list);
  }, (error) => {
    console.error("Error subscribing to headers:", error);
    callback([], error);
  });
}

export async function addHeader(userId, name, order = 0) {
  if (!userId) throw new Error("User ID is required to add a header.");
  const docData = {
    userId,
    name,
    order: Number(order)
  };
  const docRef = await addDoc(collection(db, "weekly_status_headers"), docData);
  return docRef.id;
}

export async function updateHeader(id, data) {
  const docRef = doc(db, "weekly_status_headers", id);
  await updateDoc(docRef, data);
}

export async function deleteHeader(id) {
  // Delete the header doc
  const docRef = doc(db, "weekly_status_headers", id);
  await deleteDoc(docRef);

  // Clean up any items belonging to this header
  try {
    const q = query(collection(db, "weekly_status_items"), where("headerId", "==", id));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const batch = writeBatch(db);
      snap.docs.forEach((itemDoc) => {
        batch.delete(itemDoc.ref);
      });
      await batch.commit();
    }
  } catch (err) {
    console.error("Failed to clean up items for deleted header:", err);
  }
}

// --- Item Service Operations ---

export function subscribeToWeeklyItems(userId, weekDate, callback) {
  if (!userId || !weekDate) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, "weekly_status_items"), 
    where("userId", "==", userId),
    where("weekDate", "==", weekDate)
  );
  return onSnapshot(q, (snapshot) => {
    const list = [];
    snapshot.forEach((doc) => {
      list.push({ id: doc.id, ...doc.data() });
    });
    list.sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
    callback(list);
  }, (error) => {
    console.error("Error subscribing to weekly items:", error);
    callback([], error);
  });
}

export async function addWeeklyItem(userId, data) {
  if (!userId) throw new Error("User ID is required to add a weekly item.");
  const itemData = {
    ...data,
    userId,
    isStarred: Boolean(data.isStarred),
    timestamp: new Date().toISOString()
  };
  const docRef = await addDoc(collection(db, "weekly_status_items"), itemData);
  return docRef.id;
}

export async function updateWeeklyItem(id, data) {
  const docRef = doc(db, "weekly_status_items", id);
  await updateDoc(docRef, data);

  // If item or notes updated, synchronize to any forward-linked item
  if (data.item !== undefined || data.notes !== undefined) {
    try {
      const q = query(
        collection(db, "weekly_status_items"),
        where("sourceItemId", "==", id)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const batch = writeBatch(db);
        snap.docs.forEach((d) => {
          const syncData = {};
          if (data.item !== undefined) syncData.item = data.item;
          if (data.notes !== undefined) syncData.notes = data.notes;
          batch.update(d.ref, syncData);
        });
        await batch.commit();
      }
    } catch (err) {
      console.error("Failed to sync updates to forwarded item:", err);
    }
  }
}

export async function deleteWeeklyItem(id) {
  const docRef = doc(db, "weekly_status_items", id);
  await deleteDoc(docRef);

  // Also clean up any auto-forwarded item linked to this source item
  try {
    const q = query(
      collection(db, "weekly_status_items"),
      where("sourceItemId", "==", id)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const batch = writeBatch(db);
      snap.docs.forEach((d) => {
        batch.delete(d.ref);
      });
      await batch.commit();
    }
  } catch (err) {
    console.error("Failed to clean up forwarded item on delete:", err);
  }
}

export async function toggleStarWeeklyItem(userId, item) {
  if (!userId || !item || !item.id) throw new Error("Item and User ID required to toggle star.");

  const newStarred = !item.isStarred;
  const itemRef = doc(db, "weekly_status_items", item.id);
  await updateDoc(itemRef, { isStarred: newStarred });

  const nextWeekDate = getNextWeekDate(item.weekDate);

  if (newStarred) {
    // Check if next week item already exists (either by sourceItemId or identical header & item text)
    const q = query(
      collection(db, "weekly_status_items"),
      where("userId", "==", userId),
      where("weekDate", "==", nextWeekDate),
      where("sourceItemId", "==", item.id)
    );
    const snap = await getDocs(q);
    if (snap.empty) {
      // Fallback check by headerId and item text
      const qFallback = query(
        collection(db, "weekly_status_items"),
        where("userId", "==", userId),
        where("weekDate", "==", nextWeekDate),
        where("headerId", "==", item.headerId)
      );
      const snapFallback = await getDocs(qFallback);
      const alreadyExists = snapFallback.docs.some(docSnap => docSnap.data().item === item.item);

      if (!alreadyExists) {
        await addDoc(collection(db, "weekly_status_items"), {
          userId,
          weekDate: nextWeekDate,
          headerId: item.headerId,
          item: item.item,
          notes: item.notes || "",
          isStarred: false,
          sourceItemId: item.id,
          timestamp: new Date().toISOString()
        });
      }
    }
  } else {
    // If unstarring, remove the auto-forwarded item in next week
    const q = query(
      collection(db, "weekly_status_items"),
      where("userId", "==", userId),
      where("weekDate", "==", nextWeekDate),
      where("sourceItemId", "==", item.id)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const batch = writeBatch(db);
      snap.docs.forEach((d) => {
        batch.delete(d.ref);
      });
      await batch.commit();
    }
  }
}
